import type { TenantWithSensitive } from "@am/shared";

// État de formulaire PARTAGÉ entre création et édition. Superset : chaque page
// n'utilise que les champs pertinents (ex. `slug` éditable seulement en création).
export interface TenantFormState {
  // 1. Identité & accès
  name: string;
  slug: string;
  email: string;
  send_credentials_email: boolean; // création: email de bienvenue ; édition: envoyer le nouveau mdp
  password: string; // création: mdp admin ; édition: nouveau mdp (vide = inchangé)
  generated_password: string | null;

  // 2. Intelligence
  openai_api_key: string;
  llm_model: string;
  rate_limit_per_minute: number;
  business_info: string;

  // 3. Comportement & outils
  persona: string;
  has_catalog: boolean;
  hide_out_of_stock: boolean;
  wc_url: string;
  wc_consumer_key: string;
  wc_consumer_secret: string;
  lead_capture: boolean;
  lead_notification_email: string;

  // 4. Apparence du widget
  bot_name: string;
  welcome_message: string;
  placeholder_text: string;
  primary_color: string;
  secondary_color: string;
  widget_size: string;
  language: string;
  position: string;
  logo_url: string;
  widget_icon_url: string;
  auto_open: boolean;
  powered_by: boolean;

  // 5. Sécurité & permissions
  allowed_origins: string;
  can_customize_widget: boolean;
}

export type StepId =
  | "identity"
  | "intelligence"
  | "behavior"
  | "appearance"
  | "security";

export interface StepDef {
  id: StepId;
  title: string;
  desc: string;
}

export const STEPS: StepDef[] = [
  { id: "identity", title: "Identité & accès", desc: "Nom du client, connexion et mot de passe administrateur." },
  { id: "intelligence", title: "Intelligence", desc: "Modèle de langage, clé API et contexte de l'entreprise." },
  { id: "behavior", title: "Comportement & outils", desc: "Ton de l'assistant, catalogue produits et capture de leads." },
  { id: "appearance", title: "Apparence du widget", desc: "Nom, couleurs, position et textes affichés dans le widget." },
  { id: "security", title: "Sécurité & permissions", desc: "Domaines autorisés et droits d'édition du client." },
];

export const DEFAULT_ALLOWED_ORIGINS = `https://ai-assistant.amsolutions.tech
https://www.ai-assistant.amsolutions.tech
https://api.ai-assistant.amsolutions.tech`;

export function emptyForm(): TenantFormState {
  return {
    name: "",
    slug: "",
    email: "",
    send_credentials_email: true,
    password: "",
    generated_password: null,
    openai_api_key: "",
    llm_model: "gpt-4o-mini",
    rate_limit_per_minute: 5,
    business_info: "",
    persona: "conseil",
    has_catalog: false,
    hide_out_of_stock: true,
    wc_url: "",
    wc_consumer_key: "",
    wc_consumer_secret: "",
    lead_capture: false,
    lead_notification_email: "",
    bot_name: "",
    welcome_message: "",
    placeholder_text: "",
    primary_color: "#000000",
    secondary_color: "#ffffff",
    widget_size: "medium",
    language: "fr",
    position: "bottom-right",
    logo_url: "",
    widget_icon_url: "",
    auto_open: true,
    powered_by: true,
    allowed_origins: DEFAULT_ALLOWED_ORIGINS,
    can_customize_widget: true,
  };
}

export function formFromTenant(t: TenantWithSensitive): TenantFormState {
  const wc = t.widget_config;
  return {
    ...emptyForm(),
    name: t.name ?? "",
    slug: t.slug ?? "",
    email: t.email ?? "",
    send_credentials_email: false,
    password: "",
    generated_password: null,
    openai_api_key: "",
    llm_model: t.llm_model ?? "gpt-4o-mini",
    rate_limit_per_minute: t.rate_limit_per_minute ?? 5,
    business_info: t.business_info ?? "",
    persona: (wc.persona || "conseil") as string,
    has_catalog: wc.has_catalog === true,
    hide_out_of_stock: wc.hide_out_of_stock !== false,
    wc_url: t.wc_url ?? "",
    wc_consumer_key: "",
    wc_consumer_secret: "",
    lead_capture: wc.lead_capture === true,
    lead_notification_email: wc.lead_notification_email ?? "",
    bot_name: wc.bot_name ?? "",
    welcome_message: wc.welcome_message ?? "",
    placeholder_text: wc.placeholder_text ?? "",
    primary_color: wc.primary_color ?? "#000000",
    secondary_color: wc.secondary_color ?? "#ffffff",
    widget_size: (wc.widget_size ?? "medium") as string,
    language: (wc.language ?? "fr") as string,
    position: (wc.position ?? "bottom-right") as string,
    logo_url: wc.logo_url ?? "",
    widget_icon_url: wc.widget_icon_url ?? "",
    auto_open: wc.auto_open !== false,
    powered_by: wc.powered_by !== false,
    allowed_origins: (wc.allowed_origins ?? []).join("\n"),
    can_customize_widget: t.can_customize_widget !== false,
  };
}

// Portion widget_config commune aux deux payloads (create / update).
export function buildWidgetConfig(form: TenantFormState) {
  return {
    bot_name: form.bot_name.trim() || undefined,
    welcome_message: form.welcome_message.trim() || undefined,
    primary_color: form.primary_color.trim() || undefined,
    secondary_color: form.secondary_color.trim() || undefined,
    widget_size: form.widget_size as "small" | "medium" | "large",
    language: form.language as "fr" | "en" | "ar" | "es" | "de",
    position: form.position as "bottom-right" | "bottom-left",
    persona: form.persona as "commerce" | "conseil" | "support",
    has_catalog: form.has_catalog,
    hide_out_of_stock: form.hide_out_of_stock,
    lead_capture: form.lead_capture,
    lead_notification_email: form.lead_notification_email.trim(),
    placeholder_text: form.placeholder_text.trim() || null,
    logo_url: form.logo_url.trim() || null,
    widget_icon_url: form.widget_icon_url.trim() || null,
    auto_open: form.auto_open,
    powered_by: form.powered_by,
    allowed_origins: form.allowed_origins
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean),
  };
}
